// Server-paged module list (Hotel-Edition B1): /api/pillar-posts with platform + media-format filters;
// items, total and stats come filtered from the server. Own query key per module. ?post=<id> opens a post.
import { useEffect, useRef, useState } from "react"
import { useSearch } from "wouter"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useToast } from "@/hooks/use-toast"
import { effectiveMediaFormat } from "@shared/pillarFormats"
import type { HotelPost } from "./hotelPosts"

export interface HotelPostPage {
  items: HotelPost[]
  total: number
  stats: { total: number; pending: number; scheduled: number; posted: number; rejected: number }
}

const PAGE_SIZE = 24

export function useHotelPostList(module: string, filter: { platform: string; mediaFormat?: string }) {
  const query = useInfiniteQuery<HotelPostPage>({
    queryKey: ["/api/pillar-posts", { module, ...filter }],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(pageParam ?? 0), platform: filter.platform })
      if (filter.mediaFormat) params.set("mediaFormat", filter.mediaFormat)
      const res = await fetch(`/api/pillar-posts?${params.toString()}`, { credentials: "include" })
      if (!res.ok) throw new Error("Die Beiträge konnten nicht geladen werden.")
      return res.json()
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    refetchOnWindowFocus: "always",
  })
  const posts = query.data?.pages.flatMap((p) => p.items) ?? []
  const total = query.data?.pages[0]?.total ?? 0

  // Deep link (?post=<id>) from Cockpit, Wochenplan or Veröffentlichungsplan. A post beyond the first page is
  // fetched by id (same hotel model incl. editability) and opened only if it belongs to this module; the link
  // counts as handled once resolved (review he-b1 F3).
  const search = useSearch()
  const { toast } = useToast()
  const handled = useRef<string | null>(null)
  const [selected, setSelected] = useState<HotelPost | null>(null)
  const formats = filter.mediaFormat?.split(",") ?? null
  const inModule = (p: HotelPost) =>
    p.platform === filter.platform && (!formats || formats.includes(effectiveMediaFormat(p)))
  useEffect(() => {
    if (query.isLoading) return
    const id = new URLSearchParams(search).get("post")
    if (!id || handled.current === id) return
    const loaded = posts.find((p) => p.id === id)
    if (loaded) {
      handled.current = id
      setSelected(loaded)
      return
    }
    let cancelled = false
    fetch(`/api/pillar-posts/${encodeURIComponent(id)}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((post: HotelPost | null) => {
        if (cancelled) return
        handled.current = id
        if (post && inModule(post)) setSelected(post)
        else toast({ title: "Beitrag nicht gefunden", description: "Den verlinkten Beitrag gibt es hier nicht (mehr).", variant: "destructive" })
      })
      .catch(() => { if (!cancelled) handled.current = id })
    return () => { cancelled = true }
  }, [search, query.isLoading, posts])

  // Keep the open dialog on the freshest row (version/editability change after an edit elsewhere).
  const current = selected ? posts.find((p) => p.id === selected.id) ?? selected : null
  return { ...query, posts, total, selected: current, setSelected }
}
